/**
 * Capacity — the PO lines and the hours behind them.
 *
 * Named after the workbook it replaces. Max and I read that workbook the same way: it measures LOAD,
 * the hours an order still needs, and not capacity, the hours a vendor has. The second half needs a
 * source for available hours that nobody has yet, so the screen says "remaining hours" and does not
 * pretend to answer a question it has no data for.
 *
 * The arithmetic is NOT here. It comes from vw_capacity_line, which is the workbook's own formula set
 * written once; a screen that recomputed it would be a second opinion that could disagree.
 *
 * Only the rows in view are built (useRowWindow, 34px measured). The Sample Tracker taught this the
 * expensive way on 2026-09-09: 210 rows went up in one 218-296ms task and felt like a stutter, and
 * nothing but the row count moved the number. This table opens with 3,786.
 *
 * Everything from Mark onwards is typed by hand, exactly as it is in the workbook today — that was
 * Max's own description of the flow, and it is the half no system can fill in. It is typed IN THE ROW:
 * a dialog hid the neighbouring lines, which are the whole reason somebody is typing a mark at all.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, AlertTriangle, Pencil, FilterX, Columns3, Check, X, Pin, CalendarDays, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import i18n from './i18n';
import Header from './Header';
import TabIcon from './TabIcon';
import useAuth from './useAuth';
import { SortTh, sortRows, rowPassesColumnFilters, distinctColumnValues } from './15_SampleTracker';
import { BACKEND_URL } from './backendConfig';
import useRowWindow from './useRowWindow';
import useFrozenColumns from './useFrozenColumns';
import CapacityMonths from './CapacityMonths';
import ModuleHelp from './ModuleHelp';
import './SPHub.css';
import './Capacity.css';

const API = `${BACKEND_URL}/api/production/capacity`;
const ROW_H = 34;
const NO_W = 52;      // the # column
const ACT_W = 74;     // the Action column, frozen on the right

/* Declared widths, because the table is windowed: with an automatic layout the browser re-measures
   the columns against a different slice of rows on every scroll step and the whole table shifts under
   the cursor. Same reason Style Hub and Product Line declare theirs. The table is then given the SUM
   of them as an explicit width — a fixed-layout table with `width: auto` still stretches to fill a
   wider container, which quietly hands every column a few extra pixels and undoes the point. */
const COLUMNS = [
  /* Which source the twelve Master-owned columns on this row are being read from. First, because it
     qualifies everything to its right: 2,595 of 3,786 lines have no Master Sheet row at all today,
     and a screen that showed the workbook's copy without saying so is exactly the second source Max
     asked us not to create (2026-09-10). */
  { key: 'fieldSource', labelKey: 'xCapacity.col.source', w: 92 },
  { key: 'vendorCode', labelKey: 'xCapacity.col.vendor', w: 74 },
  { key: 'season', labelKey: 'xCapacity.col.season', w: 74 },
  { key: 'vendorPo', labelKey: 'xCapacity.col.vendorPo', w: 96 },
  { key: 'cycleNo', labelKey: 'xCapacity.col.cycle', w: 118 },
  { key: 'customerPo', labelKey: 'xCapacity.col.customerPo', w: 140 },
  { key: 'destination', labelKey: 'xCapacity.col.destination', w: 104 },
  { key: 'sapCode', labelKey: 'xCapacity.col.item', w: 92 },
  { key: 'description', labelKey: 'xCapacity.col.description', w: 230 },
  { key: 'colour', labelKey: 'xCapacity.col.colour', w: 130 },
  { key: 'quantity', labelKey: 'xCapacity.col.qty', w: 72, num: true },
  { key: 'deliveryDate', labelKey: 'xCapacity.col.delivery', w: 100 },
  { key: 'skinCt', labelKey: 'xCapacity.col.skinCt', w: 78, num: true },
  { key: 'caseCt', labelKey: 'xCapacity.col.caseCt', w: 78, num: true },
  { key: 'ctSource', labelKey: 'xCapacity.col.ctFrom', w: 82 },
  { key: 'skinOutput', labelKey: 'xCapacity.col.skinOut', w: 82, num: true },
  { key: 'caseOutput', labelKey: 'xCapacity.col.caseOut', w: 82, num: true },
  { key: 'orderHours', labelKey: 'xCapacity.col.orderH', w: 88, num: true },
  { key: 'hoursDone', labelKey: 'xCapacity.col.doneH', w: 88, num: true },
  { key: 'hoursRemaining', labelKey: 'xCapacity.col.remainH', w: 92, num: true },
  { key: 'mark', labelKey: 'xCapacity.col.mark', w: 96, type: 'text', hint: 'Normal / Pull FW' },
  { key: 'fiDate', labelKey: 'xCapacity.col.fiDate', w: 110, type: 'date' },
  { key: 'result', labelKey: 'xCapacity.col.result', w: 90, type: 'text', hint: 'Pass / Fail' },
  { key: 'shippingMode', labelKey: 'xCapacity.col.ship', w: 86, type: 'text' },
  { key: 'remark', labelKey: 'xCapacity.col.remark', w: 200, type: 'text' },
  { key: 'newExfty', labelKey: 'xCapacity.col.newExfty', w: 110, type: 'date' },
];

/* The hand-filled tail. Typed in the row, so it is also the list the screen has to make sure is on
   screen before anybody can type into it. */
const EDITABLE = COLUMNS.filter((c) => c.type).map((c) => c.key);
const EDITABLE_SET = new Set(EDITABLE);

/* 'sct' | 'sheet' in the row, "SCT" | "typed" in the cell. The getter returns what the cell SHOWS,
   so the column's funnel offers the same two words a person is reading off the screen. */
// Read in the reader's language when called (render time), so a funnel offers the words on screen.
const ctSourceLabel = (r) => (r.ctSource === 'sct' ? 'SCT' : (r.ctSource ? i18n.t('xCapacity.srcTyped') : ''));
/* Three states, not two: a line with no Master row is not the same as one whose Master rows disagree
   with each other — the first is waiting for data, the second for a decision. */
const fieldSourceLabel = (r) => {
  if (r.fieldSource === 'master') return i18n.t('xCapacity.fsMaster');
  return r.masterMatch === 'many' ? i18n.t('xCapacity.fsAmbiguous') : i18n.t('xCapacity.fsFileOnly');
};
const GETTERS = Object.fromEntries(COLUMNS.map((c) => [
  c.key,
  c.key === 'ctSource' ? ctSourceLabel
    : (c.key === 'fieldSource' ? fieldSourceLabel
      : (c.num ? (r) => Number(r[c.key] ?? 0) : (r) => r[c.key] || '')),
]));

/* Which columns are on screen, and where the frozen run ends. Same shape as Master Sheet's picker:
   a map of key -> false for the HIDDEN ones, so a column added later shows up for everybody instead
   of staying invisible until they press Show all. */
/* THE GUIDE (Max 01/11: every module has one). It starts on Lines; Edit is drawn only for a person who may edit capacity,
   so that step is skipped for everybody else. */
const TOUR = [
  { selector: '.cap-tabs', title: 'tourCapacity.tabsT', body: 'tourCapacity.tabsB' },
  { selector: '.cap-stats', title: 'tourCapacity.statsT', body: 'tourCapacity.statsB' },
  { selector: '.cap-bar', title: 'tourCapacity.filtersT', body: 'tourCapacity.filtersB' },
  { selector: '.cap-bar-right', title: 'tourCapacity.colsT', body: 'tourCapacity.colsB' },
  { selector: '.cap-scroll', title: 'tourCapacity.tableT', body: 'tourCapacity.tableB' },
  { selector: '.cap-scroll td.cap-act .sp-btn', title: 'tourCapacity.editT', body: 'tourCapacity.editB' },
];

const COL_STORE = 'hevn_capacity_columns_v1';
const FREEZE_STORE = 'hevn_capacity_freeze_v1';
/* Item# is the row's anchor and stays. Unchecking everything would otherwise leave a table of row
   numbers and Edit buttons, which looks broken rather than empty. */
const COL_LOCKED = new Set(['sapCode']);
const readStore = (key, fallback) => {
  try { return JSON.parse(localStorage.getItem(key) || '') ?? fallback; } catch (e) { return fallback; }
};
const writeStore = (key, value) => {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* private mode */ }
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const n0 = (v) => (v === null || v === undefined || v === '' ? '—' : Number(v).toLocaleString('en-GB', { maximumFractionDigits: 0 }));
const n2 = (v) => (v === null || v === undefined || v === '' ? '—' : Number(v).toFixed(2));
const ym = (v) => String(v || '').slice(0, 7);

function Stat({ label, value, sub, tone }) {
  return (
    <div className={`sp-stat ${tone || ''}`}>
      <div className="sp-stat-value">{value}</div>
      <div className="sp-stat-label">{label}</div>
      {sub && <div className="sp-stat-sub">{sub}</div>}
    </div>
  );
}

/**
 * Delivery date as a Year > Month tree, the shape Max sent from Excel.
 *
 * Not a flat list of 500-odd dates: the question this filter answers is "what is due this quarter",
 * and a list of every individual day makes that a scrolling exercise. Days stay available on the
 * Delivery column's own funnel, which already draws the full Year > Month > Day tree.
 */
function DeliveryFilter({ tree, selected, onChange }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  /* Years start CLOSED. Opened, this is 24 month rows, and the two lines that say which years exist
     are what somebody reads first. */
  const [expanded, setExpanded] = useState({});

  const allKeys = useMemo(() => tree.flatMap((y) => y.months.map((m) => m.key)), [tree]);
  /* `selected` is null for "no filter" and a Set otherwise — an EMPTY set means no months, the way
     Excel's (Select All) works when you untick it. Collapsing the two would leave no way to ask for
     one month: unticking the last year would read as "all" and hand back the whole table. */
  const on = selected || new Set(allKeys);
  const label = !selected ? t('xCapacity.allDeliveryDates')
    : (selected.size === 0 ? t('xCapacity.noMonths')
      : (selected.size === 1 ? [...selected][0] : t('xCapacity.monthsOf', { n: selected.size, total: allKeys.length })));

  const commit = (next) => onChange(next.size >= allKeys.length ? null : next);
  const setMany = (keys, add) => {
    const next = new Set(on);
    keys.forEach((k) => (add ? next.add(k) : next.delete(k)));
    commit(next);
  };
  const yearKeys = (y) => y.months.map((m) => m.key);
  const yearState = (y) => {
    const n = yearKeys(y).filter((k) => on.has(k)).length;
    return n === 0 ? 'off' : (n === y.months.length ? 'all' : 'some');
  };
  const allState = on.size === 0 ? 'off' : (on.size >= allKeys.length ? 'all' : 'some');

  return (
    <div className="sp-colmenu-wrap">
      <button type="button" className={`sp-btn ${selected ? 'cap-on' : ''}`} onClick={() => setOpen((v) => !v)}>
        <CalendarDays size={13} /> {label}
      </button>
      {open && (
        <>
          {/* Click-away, not a blur handler: a blur fires before the checkbox it was heading for
              receives the click, which closed Master Sheet's menu on every tick. */}
          <div className="sp-colmenu-back" onClick={() => setOpen(false)} />
          <div className="sp-colmenu cap-datemenu">
            <div className="sp-colmenu-h">{t('xCapacity.deliveryDate')}</div>
            <div className="cap-datemenu-body">
              <label className="cap-year cap-all">
                <span className="cap-twisty-gap" />
                <input
                  type="checkbox"
                  checked={allState !== 'off'}
                  ref={(el) => { if (el) el.indeterminate = allState === 'some'; }}
                  onChange={(e) => onChange(e.target.checked ? null : new Set())}
                />
                <span>{t('xCapacity.selectAll')}</span>
              </label>
              {tree.map((y) => {
                const state = yearState(y);
                const isOpen = expanded[y.year] === true;
                return (
                  <div key={y.year}>
                    <label className="cap-year">
                      <button
                        type="button"
                        className={`cap-twisty ${isOpen ? 'open' : ''}`}
                        title={isOpen ? t('xCapacity.collapse') : t('xCapacity.expand')}
                        onClick={(e) => { e.preventDefault(); setExpanded((p) => ({ ...p, [y.year]: !isOpen })); }}
                      >
                        <ChevronRight size={12} />
                      </button>
                      <input
                        type="checkbox"
                        checked={state !== 'off'}
                        ref={(el) => { if (el) el.indeterminate = state === 'some'; }}
                        onChange={(e) => setMany(yearKeys(y), e.target.checked)}
                      />
                      <span>{y.year}</span>
                      <em>{y.count.toLocaleString('en-GB')}</em>
                    </label>
                    {isOpen && y.months.map((m) => (
                      <label key={m.key} className="cap-month">
                        <input
                          type="checkbox"
                          checked={on.has(m.key)}
                          onChange={(e) => setMany([m.key], e.target.checked)}
                        />
                        <span>{m.label}</span>
                        <em>{m.count.toLocaleString('en-GB')}</em>
                      </label>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function Capacity() {
  const { t } = useTranslation();
  /* `functions` is a list of KEYS with no level on them, so `f === 'capacity'` was true for a viewer
     as well: everyone with view saw the pencil and got a 403 on the first save. The level lives in
     `permissions`, which is { key: 'view' | 'edit' }. */
  const { permissions } = useAuth();
  const mayEdit = (permissions || {}).capacity === 'edit';

  const [lines, setLines] = useState([]);
  const [options, setOptions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  /* Opens on ALL, not on open-only: Max, 2026-09-10. A screen whose first number already excludes
     rows is a screen whose totals disagree with the workbook it replaces. */
  const [filters, setFilters] = useState({ vendor: '', cycle: '', status: 'all', search: '' });
  // null = every month. A Set = exactly those months, and an empty one means none.
  const [months, setMonths] = useState(null);
  const [sort, setSort] = useState({ key: 'deliveryDate', dir: 'asc' });
  const [colFilters, setColFilters] = useState({});
  const [openFilter, setOpenFilter] = useState(null);

  const [colVisible, setColVisible] = useState(() => readStore(COL_STORE, {}) || {});
  const [freezeThrough, setFreezeThrough] = useState(() => readStore(FREEZE_STORE, '') || '');
  const [colMenu, setColMenu] = useState(false);
  /* Two questions, two tabs. "How many hours do these orders need" is the line table; "does the vendor
     have them" is its own view with its own row shape — one row per vendor per month, not per PO line. */
  const [tab, setTab] = useState('lines');

  const [edit, setEdit] = useState(null);          // { id, form }
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await fetch(API, { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || i18n.t('xCapacity.loadFailed', { status: res.status }));
      setLines(data.lines || []);
      setOptions(data.options || {});
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  /* ---- columns on screen, and the frozen run ---- */
  const visibleColumns = useMemo(
    () => COLUMNS.filter((c) => COL_LOCKED.has(c.key) || colVisible[c.key] !== false),
    [colVisible],
  );
  const setColumns = (next) => { setColVisible(next); writeStore(COL_STORE, next); };
  const hiddenCount = COLUMNS.length - visibleColumns.length;
  const setFreeze = (key) => { setFreezeThrough(key); writeStore(FREEZE_STORE, key); };

  /* Freezing is a RUN, not a set of columns anyone can tick individually: a frozen column has to sit
     against the frozen ones beside it, so pinning column 9 alone would mean sliding it over the top
     of 1-8. Excel's Freeze Panes works the same way — you name the boundary, not the members. So the
     picker asks "freeze through here" and the run is every visible column up to that one. */
  const freezeIdx = visibleColumns.findIndex((c) => c.key === freezeThrough);
  const frozenCount = freezeIdx >= 0 ? freezeIdx + 1 : 0;
  const [tableRef, lefts] = useFrozenColumns(1 + frozenCount, [visibleColumns, frozenCount]);

  const tableWidth = NO_W + ACT_W + visibleColumns.reduce((a, c) => a + c.w, 0);

  /* ---- the delivery tree, built from every line so the shape does not change under the filter ---- */
  const monthTree = useMemo(() => {
    const years = new Map();
    lines.forEach((l) => {
      const key = ym(l.deliveryDate);
      if (key.length !== 7) return;
      const year = key.slice(0, 4);
      if (!years.has(year)) years.set(year, new Map());
      const m = years.get(year);
      m.set(key, (m.get(key) || 0) + 1);
    });
    return [...years.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([year, m]) => ({
      year,
      count: [...m.values()].reduce((a, b) => a + b, 0),
      months: [...m.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([key, count]) => ({
        key, count, label: MONTHS[Number(key.slice(5, 7)) - 1] || key,
      })),
    }));
  }, [lines]);

  /* What the TOOLBAR leaves, before any column funnel has had a say. Kept separate because it is
     what the funnels have to be offered: a menu built from the already-funnelled rows can only ever
     list the values you kept, so unticking "typed" made "typed" vanish from the menu and there was no
     way back to it (Max, 2026-09-10). Every other table in the portal passes the pre-funnel rows for
     exactly this reason. */
  const toolbarRows = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    return lines.filter((l) => (
      (!filters.vendor || l.vendorCode === filters.vendor)
      && (!filters.cycle || l.cycleNo === filters.cycle)
      && (!months || months.has(ym(l.deliveryDate)))
      && (filters.status === 'all' || (filters.status === 'open' ? l.isOpen : !l.isOpen))
      && (!q || [l.sapCode, l.description, l.vendorPo, l.customerPo, l.colour]
        .some((v) => String(v || '').toLowerCase().includes(q)))
    ));
  }, [lines, filters, months]);

  const filtered = useMemo(() => {
    const afterCols = Object.keys(colFilters).length
      ? toolbarRows.filter((r) => rowPassesColumnFilters(r, colFilters, GETTERS)) : toolbarRows;
    return sortRows(afterCols, sort, GETTERS);
  }, [toolbarRows, colFilters, sort]);

  /* The cards describe the rows on screen, not the whole table. A total that ignores the filter above
     it is the mismatch that sent Miley looking for missing work on Product Line. */
  const totals = useMemo(() => filtered.reduce((a, l) => ({
    qty: a.qty + l.quantity,
    order: a.order + l.orderHours,
    done: a.done + l.hoursDone,
    remain: a.remain + l.hoursRemaining,
    open: a.open + (l.isOpen ? 1 : 0),
  }), { qty: 0, order: 0, done: 0, remain: 0, open: 0 }), [filtered]);

  const [scrollBox, win] = useRowWindow({
    rowHeight: ROW_H,
    total: filtered.length,
    resetKey: `${sort.key}${sort.dir}|${JSON.stringify(filters)}|${months ? [...months].join() : 'all'}|${JSON.stringify(colFilters)}`,
  });
  const visible = win.windowed ? filtered.slice(win.first, win.first + win.count) : filtered;
  const spacerSpan = visibleColumns.length + 2;

  /* ---- inline edit ---- */
  const hiddenEditable = EDITABLE.filter((k) => !visibleColumns.some((c) => c.key === k));
  // The first typed column that is actually on screen takes the caret, so Edit lands somewhere.
  const firstTypedVisible = EDITABLE.find((k) => visibleColumns.some((c) => c.key === k)) || '';
  const startEdit = (line) => {
    setEditError('');
    setEdit({ id: line.id, form: Object.fromEntries(EDITABLE.map((k) => [k, line[k] || ''])) });
  };
  const setField = (k) => (e) => setEdit((p) => ({ ...p, form: { ...p.form, [k]: e.target.value } }));
  const saveEdit = async () => {
    if (!edit || saving) return;
    setSaving(true); setEditError('');
    try {
      const res = await fetch(`${API}/${edit.id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(edit.form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('xCapacity.saveFailed', { status: res.status }));
      setLines((prev) => prev.map((l) => (l.id === data.line.id ? data.line : l)));
      setEdit(null);
    } catch (err) {
      setEditError(err.message);
    } finally {
      setSaving(false);
    }
  };
  const editKeys = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); saveEdit(); }
    if (e.key === 'Escape') { e.preventDefault(); setEdit(null); setEditError(''); }
  };

  const thProps = (key) => ({
    sortKey: key,
    sort,
    onSort: (k) => setSort((p) => (p.key === k ? { key: k, dir: p.dir === 'asc' ? 'desc' : 'asc' } : { key: k, dir: 'asc' })),
    filterValues: openFilter?.key === key ? distinctColumnValues(toolbarRows, key, colFilters, GETTERS) : undefined,
    filterSelected: colFilters[key],
    filterOpen: openFilter?.key === key,
    filterAnchor: openFilter?.key === key ? openFilter.anchor : null,
    onOpenFilter: (k, anchor) => setOpenFilter((p) => (p && p.key === k ? null : { key: k, anchor })),
    onCloseFilter: () => setOpenFilter(null),
    onApplyFilter: (k, values, allValues) => {
      setColFilters((prev) => {
        const next = { ...prev };
        const isAll = allValues && allValues.length && values.length >= allValues.length;
        if (!values.length || isAll) delete next[k]; else next[k] = values;
        return next;
      });
      setOpenFilter(null);
    },
  });

  const cell = (l, c) => {
    if (c.key === 'skinCt' || c.key === 'caseCt') return n2(l[c.key]);
    if (c.key === 'orderHours' || c.key === 'hoursDone' || c.key === 'hoursRemaining') return n0(l[c.key]);
    if (c.num) return n0(l[c.key]);
    /* Where a cycle time came from, said plainly. After a workbook row carried 1142 hours a piece for
       months, "somebody typed this" is worth a word on the row rather than a note in a file. */
    if (c.key === 'ctSource') {
      if (!l.ctSource) return '—';
      return <span className={`cap-src cap-src-${l.ctSource}`}>{l.ctSource === 'sct' ? 'SCT' : t('xCapacity.srcTyped')}</span>;
    }
    /* Where the row's identity columns are read from. A row on the Master Sheet whose workbook copy
       says something else carries the disagreement in its tooltip rather than hiding it: on the 789
       linked lines today that is 529 destinations, 81 descriptions, 77 colours and 58 quantities. */
    if (c.key === 'fieldSource') {
      const onMaster = l.fieldSource === 'master';
      const diff = l.sheetDiff && Object.entries(l.sheetDiff);
      return (
        <span
          className={`cap-src cap-fs-${onMaster ? 'master' : (l.masterMatch === 'many' ? 'many' : 'sheet')}`}
          title={diff && diff.length
            ? t('xCapacity.fsWorkbookSays', { diff: diff.map(([k, v]) => `${k} ${v}`).join(' · ') })
            : (onMaster ? t('xCapacity.fsReadFromMaster')
              : (l.masterMatch === 'many'
                ? t('xCapacity.fsManyDisagree')
                : t('xCapacity.fsNoMaster')))}
        >
          {onMaster ? t('xCapacity.fsMasterShort') : t('xCapacity.fsFileShort')}
          {diff && diff.length ? <em>{diff.length}</em> : null}
        </span>
      );
    }
    return l[c.key] || '—';
  };

  const clearAll = () => {
    setFilters({ vendor: '', cycle: '', status: 'all', search: '' });
    setMonths(null);
    setColFilters({});
  };

  return (
    <div className="sp-page cap-page">
      <Header />
      {/* .sp-main, not a shell of my own: it carries the 270px offset that keeps the page out from
          under an open sidebar, and the page padding, in the one place every other screen uses. The
          first version invented `sp-shell` and had neither, so the first stat card and the first
          table columns sat underneath the sidebar (Max, 2026-09-09: "bị lỗi width của bảng"). */}
      <div className="sp-main">
        <div className="mh-titlerow">
          <div className="cap-head">
            <h2>{t('xCapacity.title')}</h2>
            <p>{tab === 'lines' ? t('xCapacity.introLines') : t('xCapacity.introMonths')}</p>
          </div>
          <ModuleHelp steps={TOUR} tourKey="capacity" ready={!loading} onBeforeTour={() => setTab('lines')} />
        </div>

        <div className="cap-tabs view-tabs" role="tablist">
          <button type="button" role="tab" aria-selected={tab === 'lines'} className={`sp-btn ${tab === 'lines' ? 'cap-on' : ''}`} onClick={() => setTab('lines')}>
            <TabIcon k="lines" />{t('xCapacity.tabLines')}
          </button>
          <button type="button" role="tab" aria-selected={tab === 'months'} className={`sp-btn ${tab === 'months' ? 'cap-on' : ''}`} onClick={() => setTab('months')}>
            <TabIcon k="months" />{t('xCapacity.tabMonths')}
          </button>
        </div>

        {tab === 'months' ? <CapacityMonths mayEdit={mayEdit} /> : (
        <>

        {!!error && <div className="sp-error"><AlertTriangle size={14} /> {error}</div>}

        <div className="sp-stat-row cap-stats">
          <Stat label={t('xCapacity.statLines')} value={n0(filtered.length)} sub={t('xCapacity.stillOpen', { n: n0(totals.open) })} />
          <Stat label={t('xCapacity.statQuantity')} value={n0(totals.qty)} sub={t('xCapacity.pcs')} />
          <Stat label={t('xCapacity.statOrderHours')} value={n0(totals.order)} />
          <Stat label={t('xCapacity.statHoursDone')} value={n0(totals.done)} tone="ok" />
          <Stat label={t('xCapacity.statHoursRemaining')} value={n0(totals.remain)} tone="warn" />
        </div>

        <div className="cap-bar">
          <select value={filters.vendor} onChange={(e) => setFilters((p) => ({ ...p, vendor: e.target.value }))}>
            <option value="">{t('xCapacity.allVendors')}</option>
            {(options.vendors || []).map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          <DeliveryFilter tree={monthTree} selected={months} onChange={setMonths} />
          <select value={filters.cycle} onChange={(e) => setFilters((p) => ({ ...p, cycle: e.target.value }))}>
            <option value="">{t('xCapacity.allCycles')}</option>
            {(options.cycles || []).map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          <select value={filters.status} onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}>
            <option value="all">{t('xCapacity.allLines')}</option>
            <option value="open">{t('xCapacity.openOnly')}</option>
            <option value="done">{t('xCapacity.finished')}</option>
          </select>
          <input
            value={filters.search}
            onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))}
            placeholder={t('xCapacity.searchPh')}
          />
          <button type="button" className="sp-btn" onClick={clearAll}>
            <FilterX size={13} /> {t('xCapacity.clear')}
          </button>

          <div className="cap-bar-right">
            <div className="sp-colmenu-wrap">
              <button type="button" className="sp-btn" onClick={() => setColMenu((v) => !v)}>
                <Columns3 size={13} /> {t('xCapacity.columns')}
                {hiddenCount > 0 && <em>&nbsp;{t('xCapacity.nHidden', { n: hiddenCount })}</em>}
              </button>
              {colMenu && (
                <>
                  <div className="sp-colmenu-back" onClick={() => setColMenu(false)} />
                  <div className="sp-colmenu cap-colmenu">
                    <div className="sp-colmenu-h">
                      {t('xCapacity.showColumns')}
                      <button type="button" onClick={() => setColumns({})}>{t('xCapacity.showAll')}</button>
                    </div>
                    {/* The pin says where the frozen run ENDS, because a frozen column has to sit
                        against its frozen neighbours — see the note by freezeIdx. */}
                    <div className="cap-colmenu-note">
                      <Pin size={11} /> {t('xCapacity.pinNote')}
                      {frozenCount > 0 && (
                        <button type="button" onClick={() => setFreeze('')}>{t('xCapacity.unfreeze')}</button>
                      )}
                    </div>
                    {COLUMNS.map((c) => {
                      const locked = COL_LOCKED.has(c.key);
                      const shown = locked || colVisible[c.key] !== false;
                      return (
                        <label key={c.key} className={locked ? 'locked' : ''}>
                          <input
                            type="checkbox"
                            checked={shown}
                            disabled={locked}
                            onChange={(e) => setColumns({ ...colVisible, [c.key]: e.target.checked })}
                          />
                          <span className={EDITABLE_SET.has(c.key) ? 'cap-typed-dot' : 'cap-sys-dot'} />
                          <span className="cap-colname">{t(c.labelKey)}</span>
                          <button
                            type="button"
                            className={`cap-pin ${freezeThrough === c.key ? 'on' : ''}`}
                            title={shown ? t('xCapacity.freezeThrough') : t('xCapacity.hiddenNoFreeze')}
                            disabled={!shown}
                            onClick={(e) => {
                              e.preventDefault();
                              setFreeze(freezeThrough === c.key ? '' : c.key);
                            }}
                          >
                            <Pin size={12} />
                          </button>
                        </label>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
            <button type="button" className="sp-btn" onClick={load} disabled={loading}>
              <RefreshCw size={13} /> {loading ? t('xCapacity.loading') : t('xCapacity.reload')}
            </button>
          </div>
        </div>

        {!!editError && <div className="sp-error"><AlertTriangle size={14} /> {editError}</div>}
        {!!edit && hiddenEditable.length > 0 && (
          <div className="cap-notice">
            {t('xCapacity.hiddenTyped', { count: hiddenEditable.length })}
            <button
              type="button"
              onClick={() => setColumns(Object.fromEntries(
                Object.entries(colVisible).filter(([k]) => !EDITABLE_SET.has(k)),
              ))}
            >
              {t('xCapacity.showThem')}
            </button>
          </div>
        )}

        <div className="cap-scroll" ref={scrollBox}>
          <table className="cap-table" ref={tableRef} style={{ width: tableWidth }}>
            <colgroup>
              <col style={{ width: NO_W }} />
              {visibleColumns.map((c) => <col key={c.key} style={{ width: c.w }} />)}
              <col style={{ width: ACT_W }} />
            </colgroup>
            <thead>
              <tr>
                <th className="cap-no sticky-col" style={{ left: lefts[0] || 0 }}>#</th>
                {visibleColumns.map((c, i) => {
                  const frozen = i < frozenCount;
                  return (
                    <SortTh
                      key={c.key}
                      label={t(c.labelKey)}
                      {...thProps(c.key)}
                      className={[
                        c.num ? 'cap-num' : '',
                        frozen ? 'sticky-col' : '',
                        frozen && i === frozenCount - 1 ? 'cap-freeze-edge' : '',
                      ].filter(Boolean).join(' ')}
                      style={frozen ? { left: lefts[i + 1] } : undefined}
                    />
                  );
                })}
                <th className="cap-act">{t('xCapacity.colAction')}</th>
              </tr>
            </thead>
            <tbody>
              {win.windowed && win.padTop > 0 && (
                <tr className="cap-spacer" style={{ height: win.padTop }}><td colSpan={spacerSpan} /></tr>
              )}
              {visible.map((l, i) => {
                const editingThis = edit && edit.id === l.id;
                return (
                  <tr key={l.id} className={`${l.isOpen ? '' : 'cap-done'} ${editingThis ? 'cap-editing' : ''}`}>
                    <td className="cap-no sticky-col" style={{ left: lefts[0] || 0 }}>
                      {(win.windowed ? win.first : 0) + i + 1}
                    </td>
                    {visibleColumns.map((c, ci) => {
                      const frozen = ci < frozenCount;
                      const cls = [
                        c.num ? 'cap-num' : '',
                        frozen ? 'sticky-col' : '',
                        frozen && ci === frozenCount - 1 ? 'cap-freeze-edge' : '',
                      ].filter(Boolean).join(' ');
                      if (editingThis && EDITABLE_SET.has(c.key)) {
                        return (
                          <td key={c.key} className={`${cls} cap-cell-edit`} style={frozen ? { left: lefts[ci + 1] } : undefined}>
                            <input
                              type={c.type === 'date' ? 'date' : 'text'}
                              value={edit.form[c.key]}
                              placeholder={c.hint || ''}
                              onChange={setField(c.key)}
                              onKeyDown={editKeys}
                              /* eslint-disable-next-line jsx-a11y/no-autofocus */
                              autoFocus={c.key === firstTypedVisible}
                            />
                          </td>
                        );
                      }
                      return (
                        <td
                          key={c.key}
                          className={cls}
                          style={frozen ? { left: lefts[ci + 1] } : undefined}
                          title={c.key === 'description' || c.key === 'remark' ? (l[c.key] || '') : undefined}
                        >
                          {cell(l, c)}
                        </td>
                      );
                    })}
                    <td className="cap-act">
                      {editingThis ? (
                        <span className="cap-rowbtns">
                          <button type="button" className="sp-btn tiny primary" title={t('xCapacity.saveEnter')} disabled={saving} onClick={saveEdit}>
                            <Check size={12} />
                          </button>
                          <button type="button" className="sp-btn tiny" title={t('xCapacity.cancelEsc')} onClick={() => { setEdit(null); setEditError(''); }}>
                            <X size={12} />
                          </button>
                        </span>
                      ) : mayEdit && (
                        <button type="button" className="sp-btn tiny" title={t('xCapacity.editRowTitle')} onClick={() => startEdit(l)}>
                          <Pencil size={12} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {win.windowed && win.padBottom > 0 && (
                <tr className="cap-spacer" style={{ height: win.padBottom }}><td colSpan={spacerSpan} /></tr>
              )}
              {!filtered.length && !loading && (
                <tr><td colSpan={spacerSpan} className="cap-empty">{t('xCapacity.noLines')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
        </>
        )}
      </div>
    </div>
  );
}
